import type { ChunkProgressReporter } from '../shared/chunk-progress';

/** SKU counts split by whether the SKU has processed sales data. */
export type SkuDataCounts = { readonly w_data: number; readonly no_data: number };

/**
 * The ean_w_data_counts_per_category_brand reference row as stored: keyed by
 * catalog access path ("Cat_Sub"), then by brand slug. Spans the full product
 * catalog, so it names categories outside the purchasable taxonomy too.
 */
export type CategoryBrandCountsByPath = Readonly<
  Record<string, Readonly<Record<string, SkuDataCounts>>>
>;

/** Where one catalog product is filed: its category path and its alternate. */
export type ProductCategoryPaths = {
  readonly ean: string;
  readonly category: string | null;
  readonly altcategory: string | null;
};

/** A catalog product as the coverage views show it. `ean` is the stored (masked) key. */
export type CatalogProduct = {
  readonly ean: string;
  readonly name: string | null;
  readonly brand: string | null;
  readonly category: string | null;
  readonly prio: number | null;
};

/**
 * The org's reach into the catalog. A product is reachable either because its
 * brand is entitled or because it sits in one of the org's category
 * pseudobrands, products put on the account without granting the whole brand.
 * For a super admin `brandSlugs` is every portal brand, so the list can run
 * to well over a thousand slugs and the read has to chunk it.
 */
export type CatalogScope = {
  readonly brandSlugs: readonly string[];
  readonly eans: readonly string[];
};

/** Which product columns a free-text search looks at. */
export interface SearchFields {
  readonly name: boolean;
  readonly category: boolean;
  readonly brand: boolean;
  readonly tags: boolean;
  readonly desc: boolean;
  readonly ean: boolean;
}

export type CatalogSearch = {
  readonly term: string;
  readonly fields: SearchFields;
  /** Narrows to products listed at any of these vendors; empty means no narrowing. */
  readonly vendorSlugs: readonly string[];
};

/**
 * A capped read. `truncated` says more products matched than `cap`, so the
 * caller can say so rather than present a slice as the whole. `products` may
 * run past `cap` by the rows fetched to detect that; callers cut to `cap`.
 */
export type CatalogPage = {
  readonly products: readonly CatalogProduct[];
  readonly truncated: boolean;
};

/**
 * Catalog reads behind the coverage suite: the global taxonomy and reference
 * data, plus product lookups within an org's scope. Every method rejects on a
 * failed read; nothing is swallowed into an empty result.
 */
export abstract class CoverageCatalogRepository {
  /** Brand slug (lowercase) to display label, for every brand in brandb. */
  abstract brandLabels(): Promise<Readonly<Record<string, string>>>;

  /**
   * Every brand eligible for the portal, lowercase and without duplicates:
   * the portal_references.available_brands row, the same list the super-admin
   * settings offer when granting an org its brands.
   */
  abstract availableBrands(): Promise<readonly string[]>;

  /** The per-category, per-brand SKU count reference row, keyed by access path. */
  abstract categoryBrandCounts(): Promise<CategoryBrandCountsByPath>;

  /** Where each of the given products is filed. Products the catalog lacks are absent. */
  abstract categoryPathsOf(
    eans: readonly string[],
    onProgress?: ChunkProgressReporter
  ): Promise<readonly ProductCategoryPaths[]>;

  /**
   * Products in the scope filed under the access path or its alternate,
   * highest prio first. Fetches past `cap` to detect truncation.
   */
  abstract productsUnderPath(
    scope: CatalogScope,
    categoryPath: string,
    cap: number
  ): Promise<CatalogPage>;

  /**
   * Products in the scope matching a free-text search, highest prio first.
   * Fetches past `cap` to detect truncation. A term that leaves nothing to
   * match on yields an empty page without a read.
   */
  abstract searchProducts(
    scope: CatalogScope,
    search: CatalogSearch,
    cap: number
  ): Promise<CatalogPage>;

  /** Catalog rows for the given stored EAN keys. */
  abstract productsByEan(eans: readonly string[]): Promise<readonly CatalogProduct[]>;

  /** A brand's products filed under the access path. */
  abstract productsOfBrandUnderPath(
    brandSlug: string,
    categoryPath: string
  ): Promise<readonly CatalogProduct[]>;
}
